import { Box, type SxProps } from "@mui/material";
import type { ReactNode, Ref } from "react";
import { type DynamicRowHeight, List, type ListImperativeAPI, type RowComponentProps } from "react-window";

type RowExtraProps<T> = {
	items: T[];
	renderItem: (item: T, index: number) => ReactNode;
	itemWrapperSx?: SxProps;
};

type Props<T> = {
	sx?: SxProps;
	items: T[];
	rowHeight: number | string | ((index: number, props: RowExtraProps<T>) => number) | DynamicRowHeight;
	renderItem: (item: T, index: number) => ReactNode;
	overscanCount?: number;
	itemWrapperSx?: SxProps;
	listRef?: Ref<ListImperativeAPI>;
	onRowsRendered?: (visibleRows: { startIndex: number; stopIndex: number }) => void;
};

const RowComponent = <T,>({ ariaAttributes, index, style, items, renderItem, itemWrapperSx }: RowComponentProps<RowExtraProps<T>>) => {
	const item = items[index];
	// react-window renders the scroller as `role="list"`; its rows are the listitems, and `aria-posinset`
	// gives each mounted row its position in the whole list, not just among the rows currently rendered.
	return (
		<Box {...ariaAttributes} style={style} sx={itemWrapperSx}>
			{renderItem(item, index)}
		</Box>
	);
};

export const VirtualizedList = <T,>({
	sx = {
		width: "100%",
		height: "100%",
	},
	items,
	renderItem,
	rowHeight,
	overscanCount,
	itemWrapperSx,
	listRef,
	onRowsRendered,
}: Props<T>) => {
	const rowProps: RowExtraProps<T> = {
		items,
		renderItem,
		itemWrapperSx,
	};

	return (
		<Box sx={sx}>
			<List
				rowComponent={RowComponent}
				rowCount={items.length}
				rowHeight={rowHeight}
				rowProps={rowProps}
				overscanCount={overscanCount}
				listRef={listRef}
				onRowsRendered={onRowsRendered}
				style={{ width: "100%", height: "100%" }}
			/>
		</Box>
	);
};
